import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/authService';
import { useVendorProfile } from '../services/vendorProfileService';
import { useVendorVerification } from './useVendorVerification';
import { useVendorTaxProfile, useVendorStripeConnect } from '../services/vendorTaxProfileService';

const DISMISS_KEY = 'vendor_getting_started_dismissed';

/**
 * Builds the start-to-finish setup checklist for vendors.
 */
export default function useVendorGettingStarted() {
  const { isVendorVerified, isVerificationPending, isLoading: verificationLoading } =
    useVendorVerification();
  const { data: profileResp, isLoading: profileLoading } = useVendorProfile();
  const profile = profileResp?.data ?? {};
  const { data: taxResp, isLoading: taxLoading } = useVendorTaxProfile({
    enabled: true,
    staleTime: 60_000,
  });
  const { data: connectResp, isLoading: connectLoading } = useVendorStripeConnect({
    enabled: true,
    staleTime: 30_000,
  });
  const connect = connectResp?.data;

  const { data: servicesData, isLoading: servicesLoading } = useQuery({
    queryKey: ['vendor-getting-started-services'],
    queryFn: async () => {
      const res = await api.get('/vendor/services', { params: { per_page: 1 } });
      return res.data;
    },
    staleTime: 60_000,
  });

  const { data: portfolioData, isLoading: portfolioLoading } = useQuery({
    queryKey: ['vendor-getting-started-portfolio'],
    queryFn: async () => {
      try {
        const res = await api.get('/user/images', {
          params: { image_type: 'portfolio', per_page: 1 },
        });
        return res.data;
      } catch {
        return { data: [] };
      }
    },
    staleTime: 60_000,
    enabled: isVendorVerified === true,
  });

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  const restore = useCallback(() => {
    try {
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* ignore */
    }
    setDismissed(false);
  }, []);

  const serviceTotal =
    servicesData?.meta?.total ??
    servicesData?.data?.total ??
    (Array.isArray(servicesData?.data) ? servicesData.data.length : 0) ??
    0;

  const portfolioItems = portfolioData?.data?.data || portfolioData?.data || [];
  const hasPortfolio = Array.isArray(portfolioItems)
    ? portfolioItems.length > 0
    : Number(portfolioData?.meta?.total || 0) > 0;

  const hasBusinessBasics = Boolean(
    (profile.business_name || '').trim() &&
      ((profile.logo_url || profile.brand_settings?.logo_url || '').trim() ||
        (profile.address || profile.city || '').trim())
  );

  const taxRow = taxResp?.data;
  const taxMeta = taxResp?.meta;
  const hasTaxProfile = Boolean(
    taxMeta?.is_complete ||
      taxRow?.completed_at ||
      ((taxRow?.legal_name || '').trim() &&
        (taxRow?.tax_residence_country || '').trim() &&
        (taxMeta?.missing_required_fields || []).length === 0)
  );

  const connectReady = Boolean(connect?.is_payout_ready);
  const connectStarted = Boolean(connect?.account_id);

  const steps = useMemo(
    () => [
      {
        id: 'verify',
        title: 'Get verified',
        description: isVerificationPending
          ? 'Your submission is under review — hang tight.'
          : 'Submit business details so you can unlock bookings and payouts.',
        href: '/vendor/verification',
        cta: isVerificationPending ? 'View status' : 'Start verification',
        done: Boolean(isVendorVerified),
      },
      {
        id: 'business',
        title: 'Complete business profile',
        description: 'Add your business name, address, and logo (used on invoices & contracts).',
        href: '/vendor/account?tab=business',
        cta: 'Edit business',
        done: hasBusinessBasics,
      },
      {
        id: 'tax',
        title: 'Set up tax profile',
        description:
          'Add legal name and tax IDs for your market — needed for compliance and payouts.',
        href: '/vendor/tax-profile',
        cta: 'Open tax profile',
        done: hasTaxProfile,
      },
      {
        id: 'connect',
        title: 'Connect Stripe for payouts',
        description: connectReady
          ? 'Stripe Connect is complete — payouts can transfer to your Express account.'
          : connectStarted
            ? `Stripe Connect status: ${(connect?.onboarding_status || 'pending').replace(/_/g, ' ')}. Finish onboarding to get payout-ready.`
            : 'Link a Stripe Express account so released earnings can be paid out automatically.',
        href: '/vendor/tax-profile',
        cta: connectReady ? 'View Connect status' : connectStarted ? 'Continue Connect' : 'Start Connect',
        done: connectReady,
      },
      {
        id: 'services',
        title: 'Add your first service',
        description: 'List what clients can book — pricing, duration, and what’s included.',
        href: '/vendor/services',
        cta: 'Add service',
        done: Number(serviceTotal) > 0,
      },
      {
        id: 'portfolio',
        title: 'Upload portfolio photos',
        description: 'Show your best work so clients trust your style before booking.',
        href: '/vendor/portfolio',
        cta: 'Open portfolio',
        done: hasPortfolio,
      },
      {
        id: 'ready',
        title: 'Watch for bookings',
        description:
          'New requests appear under Bookings. Use Invoices & Contracts when you need paperwork.',
        href: '/vendor/bookings',
        cta: 'Go to bookings',
        done: Boolean(isVendorVerified) && Number(serviceTotal) > 0 && hasTaxProfile,
      },
    ],
    [
      isVendorVerified,
      isVerificationPending,
      hasBusinessBasics,
      hasTaxProfile,
      connectReady,
      connectStarted,
      connect?.onboarding_status,
      serviceTotal,
      hasPortfolio,
    ]
  );

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;
  const isLoading =
    verificationLoading ||
    profileLoading ||
    taxLoading ||
    connectLoading ||
    servicesLoading ||
    (isVendorVerified && portfolioLoading);

  return {
    steps,
    doneCount,
    allDone,
    isLoading,
    visible: !dismissed,
    dismiss,
    restore,
  };
}
